import React, { useMemo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { frameworkKey, frameworkLabel, scorePercent } from "../utils";
import { AH_BORDER, AH_MUTED, AH_TEXT, AH_WHITE, MetaSeparator, outlinedButtonSx, primaryButtonSx } from "../theme";

const scoreLabel = (value) => {
  const percent = scorePercent(value);
  if (percent == null) return "Scan in progress";
  return `${Math.round(percent)}%`;
};

/** One entry per framework; it opens on the first account (in audit order) with a completed scan. */
const groupByFramework = (accounts) => {
  const groups = new Map();
  accounts.forEach((account) => {
    (account.scopes || []).forEach((scope) => {
      const key = frameworkKey(scope.framework);
      if (!groups.has(key)) groups.set(key, { key, framework: scope.framework, accounts: 0, scanned: 0, target: null });
      const group = groups.get(key);
      group.accounts += 1;
      if (!scope.framework?.name) return;
      if (scope.latestRunId) group.scanned += 1;
      if (!group.target || (scope.latestRunId && !group.target.scope.latestRunId)) {
        group.target = { account, scope };
      }
    });
  });
  return Array.from(groups.values());
};

const AuditComplianceDialog = ({ open, status, audit, error, onClose, onOpenScope }) => {
  const accounts = useMemo(() => (Array.isArray(audit?.accounts) ? audit.accounts : []), [audit]);
  const frameworks = useMemo(() => groupByFramework(accounts), [accounts]);
  const accountCount = audit?.accountCount ?? accounts.length;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography sx={{ fontSize: 13, color: AH_MUTED, fontWeight: 600 }}>
          {audit?.auditCode || "Assessment"}
        </Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: AH_TEXT }}>
          {status === "loading" ? "Loading assessment" : audit?.name || "Compliance view"}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {status === "loading" ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : status === "error" ? (
          <Typography sx={{ fontSize: 14, color: "#b91c1c", py: 2 }}>{error}</Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1, pb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1.5, rowGap: 0.5 }}>
              {[
                audit?.ownerEmail ? `Owner ${audit.ownerEmail}` : "No owner",
                `${accountCount} account${accountCount === 1 ? "" : "s"}`,
                `${frameworks.length} framework${frameworks.length === 1 ? "" : "s"}`,
                `Assessment score ${scoreLabel(audit?.auditScore)}`,
              ].map((item, index) => (
                <React.Fragment key={item}>
                  {index > 0 ? <MetaSeparator /> : null}
                  <Typography sx={{ fontSize: 13, color: AH_MUTED }}>{item}</Typography>
                </React.Fragment>
              ))}
            </Box>
            <Typography sx={{ fontSize: 13, color: AH_MUTED }}>
              Choose a framework. You can switch between this assessment&apos;s accounts from the compliance page.
            </Typography>
            {frameworks.length === 0 ? (
              <Typography sx={{ fontSize: 14, color: AH_TEXT, py: 2 }}>This assessment has no frameworks.</Typography>
            ) : (
              frameworks.map((group) => (
                <Box
                  key={group.key}
                  sx={{
                    border: `1px solid ${AH_BORDER}`,
                    borderRadius: "10px",
                    backgroundColor: AH_WHITE,
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: AH_TEXT }}>
                      {frameworkLabel(group.framework)}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: AH_MUTED, mt: 0.25 }}>
                      {group.scanned
                        ? `Scanned on ${group.scanned} of ${group.accounts} account${group.accounts === 1 ? "" : "s"}`
                        : "Pending Automated Scan Completion"}
                    </Typography>
                  </Box>
                  <Button
                    disabled={!group.target}
                    onClick={() => onOpenScope(group.target.account, group.target.scope)}
                    sx={{ ...primaryButtonSx, height: 34, minHeight: 34 }}
                  >
                    Open compliance
                  </Button>
                </Box>
              ))
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
              <Button onClick={onClose} sx={outlinedButtonSx}>
                Close
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuditComplianceDialog;
